import {
  ApiResponseStatus,
  ApiResponseStatusSectionDisplay,
} from 'components/ApiResponseStatusSectionDisplay/ApiResponseStatusSectionDisplay';
import { Layout } from 'components/Layout/Layout';
import { useMenuPageConfigs } from 'hooks/useMenuPageConfigs';
import { useTarteAuCitron } from 'hooks/useTarteAuCitron';
import {
  BusinessReferentsPage,
  ClientCompanyBusinessReferentsPage,
  ClientCompanyHistoryPage,
  ClientCompanyMainPage,
  ClientCompanyUsersPage,
  InvitationProcessEndPage,
  SubscriptionRequestPage,
  SubscriptionRequestSuccessPage,
} from 'pages/lazyLoadedPages';
import { NotFoundPage } from 'pages/NotFound/NotFoundPage';
import { FunctionComponent, Suspense, useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useAppDispatch } from 'redux/hooks';
import { storeLegalCorporationConfig } from 'redux/LegalCorporation/slice';
import { getSelectedPermission, UserApplication } from 'redux/User/selectors';
import { storePermissions, updateSelectedPermissionId } from 'redux/User/slice';
import { ProtectedRoute } from 'routing/ProtectedRoute';
import { ROUTES } from 'routing/routes';
import { analytics } from 'services/analytics/analytics';
import { getClientCompanyId, getPermissionId } from 'services/api/common';
import { fetchLegalCorporationConfig } from 'services/api/legalCorporationConfig/client';
import { normalizeLegalCorporationConfig } from 'services/api/legalCorporationConfig/normalizer';
import { fetchUserPermissions } from 'services/api/permissions/client';
import { useAuthenticationStatus } from 'services/authentication/useAuthenticationStatus';
import { logger } from 'services/logs/logger';

export const App: FunctionComponent = () => {
  useTarteAuCitron();
  const intl = useIntl();
  const apiResponseStatusSectionDisplayConfiguration = {
    errorStatusTitle: intl.formatMessage({
      id: 'app.error-response.title',
    }),
    errorStatusDescription: intl.formatMessage({
      id: 'app.error-response.description',
    }),
  };
  const [apiResponseStatus, setApiResponseStatus] = useState(ApiResponseStatus.LOADING);
  const authenticationStatus = useAuthenticationStatus();
  const dispatch = useAppDispatch();
  const fetchAndStoreUserPermissions = useCallback(async () => {
    try {
      const permissions = await fetchUserPermissions();
      dispatch(storePermissions(permissions));
      setApiResponseStatus(ApiResponseStatus.SUCCESS);
      let pId: string = getPermissionId();
      if (!pId) {
        const ccId = getClientCompanyId();
        if (ccId) {
          const matched = permissions.find(
            (permission) => permission.clientCompany?.id.toString() === ccId,
          );
          pId = matched ? matched.id.toString() : '';
        }
      }
      if (pId) {
        dispatch(updateSelectedPermissionId(pId));
      }
    } catch (error) {
      logger.logError('Error fetching user permissions', error);
      setApiResponseStatus(ApiResponseStatus.ERROR);
    }
  }, [dispatch]);

  const fetchAndStoreLegalCorporationConfig = useCallback(async () => {
    try {
      const config = await fetchLegalCorporationConfig();
      dispatch(storeLegalCorporationConfig(normalizeLegalCorporationConfig(config)));
    } catch (error) {
      logger.logError('Error fetching legal corporation config', error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (authenticationStatus.isLoading) {
      setApiResponseStatus(ApiResponseStatus.LOADING);
    } else {
      if (authenticationStatus.isAuthenticated) {
        fetchAndStoreUserPermissions();
        fetchAndStoreLegalCorporationConfig();
      } else {
        setApiResponseStatus(ApiResponseStatus.SUCCESS);
      }
    }
  }, [
    fetchAndStoreUserPermissions,
    fetchAndStoreLegalCorporationConfig,
    authenticationStatus.isLoading,
    authenticationStatus.isAuthenticated,
  ]);
  const menuPageConfigs = useMenuPageConfigs();

  const selectedPermission = useSelector(getSelectedPermission);

  useEffect(() => {
    if (selectedPermission) {
      analytics.setUserConfig(selectedPermission);
    }
  }, [selectedPermission]);

  return (
    <ApiResponseStatusSectionDisplay
      configuration={apiResponseStatusSectionDisplayConfiguration}
      status={apiResponseStatus}
    >
      <BrowserRouter>
        <Suspense
          fallback={
            <Layout>
              <ApiResponseStatusSectionDisplay status={ApiResponseStatus.LOADING} />
            </Layout>
          }
        >
          <Layout>
            <Routes>
              <Route path="/" element={<Navigate to={ROUTES.HOME} replace />} />
              {menuPageConfigs.map((menuPageConfig) => {
                const PageComponent = menuPageConfig.pageComponent;
                return (
                  <Route
                    key={menuPageConfig.linkPath ?? menuPageConfig.path}
                    path={menuPageConfig.path}
                    element={
                      <ProtectedRoute
                        applications={menuPageConfig.applications}
                        roles={menuPageConfig.roles}
                        isAllowedForInternalCompany={menuPageConfig.isAllowedForInternalCompany}
                      >
                        <PageComponent />
                      </ProtectedRoute>
                    }
                  />
                );
              })}
              <Route
                path={ROUTES.CLIENT_COMPANY}
                element={
                  <ProtectedRoute applications={[UserApplication.BACK_OFFICE]}>
                    <ClientCompanyMainPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CLIENT_COMPANY_USERS}
                element={
                  <ProtectedRoute applications={[UserApplication.BACK_OFFICE]}>
                    <ClientCompanyUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CLIENT_COMPANY_HISTORY}
                element={
                  <ProtectedRoute applications={[UserApplication.BACK_OFFICE]}>
                    <ClientCompanyHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.CLIENT_COMPANY_BUSINESS_REFERENTS}
                element={
                  <ProtectedRoute applications={[UserApplication.BACK_OFFICE]}>
                    <ClientCompanyBusinessReferentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ROUTES.BUSINESS_REFERENTS}
                element={
                  <ProtectedRoute applications={[UserApplication.FRONT_OFFICE]}>
                    <BusinessReferentsPage />
                  </ProtectedRoute>
                }
              />
              <Route path={ROUTES.INVITATION_PROCESS_END} element={<InvitationProcessEndPage />} />
              <Route path={ROUTES.SUBSCRIPTION_REQUEST} element={<SubscriptionRequestPage />} />
              <Route
                path={ROUTES.SUBSCRIPTION_REQUEST_SUCCESS}
                element={<SubscriptionRequestSuccessPage />}
              />
              <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
              <Route path={ROUTES.HEALTH_CHECK_FRONTEND} element={null} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Layout>
        </Suspense>
      </BrowserRouter>
    </ApiResponseStatusSectionDisplay>
  );
};
